// GRN phase 1 (2026-10-21): the new receive window (path 2 deliveries, and any open PO).
// g04 delivery note / invoice number or a photo · g06 received on · g10 every rejection gets an outcome ·
// g14 price paid typed here · g15 "Goes to" required when the line has none · the rest: still coming by a date,
// or closed (Ahmed approves). Saved by receiving.service receiveGoods → the existing receipt step (createGRN).
import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Drawer, Input, InputNumber, Modal, Radio, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ProofFile, receiveGoods, ReceiveLinePayload, RejectOutcome } from '../../api';
import CostCenterSelect from '../CostCenterSelect';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { daysBack, isoDay, money, OUTCOME_OPTIONS, outsideReceiptWindow, ProofPicker, REASON_AFTER_DAYS, REJECT_REASONS } from './shared';

interface LineState {
  arrived: number | null;
  rejected: number | null;
  reason?: string;
  outcome?: RejectOutcome;
  dueDate: Dayjs | null;
  amount: number | null;
  outcomeNote: string;
  price: number | null;
  ccId: number | null;
  restAction?: 'coming' | 'close';
  restDate: Dayjs | null;
  restReason: string;
  mrAction: 'not_needed' | 'reorder';
}

interface Props {
  open: boolean;
  /** The PO as returned by getPO (lines with qty, totalReceived, cancelledQty, prices, unit, costCenter). */
  po: any;
  onClose: () => void;
  onSaved: (grn: any) => void;
}

const MUTED = '#5f6672';
const n = (v: unknown) => Number(v ?? 0);
const round2 = (x: number) => Math.round(x * 100) / 100;
const openOf = (l: any) => round2(n(l.qty) - n(l.totalReceived) - n(l.cancelledQty));
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: MUTED, minWidth: 0 };

const ReceiveWindow: React.FC<Props> = ({ open, po, onClose, onSaved }) => {
  const compact = useCompactLayout();
  const openLines = useMemo(() => (po?.lines || []).filter((l: any) => openOf(l) > 0 && l.cancelStatus !== 'Pending'), [po]);
  const lockedLines = useMemo(() => (po?.lines || []).filter((l: any) => openOf(l) > 0 && l.cancelStatus === 'Pending'), [po]);
  const [receivedOn, setReceivedOn] = useState<Dayjs | null>(dayjs());
  const [dateReason, setDateReason] = useState('');
  const [docRef, setDocRef] = useState('');
  const [proof, setProof] = useState<ProofFile[]>([]);
  const [notes, setNotes] = useState('');
  const [st, setSt] = useState<Record<number, LineState>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !po) return;
    setReceivedOn(dayjs()); setDateReason(''); setDocRef(''); setProof([]); setNotes('');
    const expected = po.expectedDelivery ? dayjs(po.expectedDelivery) : null;
    setSt(Object.fromEntries(openLines.map((l: any) => [l.id, {
      arrived: openOf(l), rejected: 0, dueDate: null, amount: null, outcomeNote: '',
      price: l.actualUnitPrice != null ? n(l.actualUnitPrice) : n(l.unitPrice) > 0 ? n(l.unitPrice) : null,
      ccId: null, restDate: l.nextExpected ? dayjs(l.nextExpected) : expected && expected.isAfter(dayjs(), 'day') ? expected : dayjs().add(7, 'day'),
      restReason: '', mrAction: 'not_needed',
    } as LineState])));
  }, [open, po]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (id: number, patch: Partial<LineState>) => setSt((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  // What the window will save for each line, and what is still missing.
  const plan = useMemo(() => openLines.map((l: any) => {
    const s = st[l.id];
    if (!s) return null;
    const open = openOf(l);
    const arrived = s.arrived || 0;
    const rejected = s.rejected || 0;
    const accepted = round2(s.outcome === 'discount' ? arrived : arrived - rejected);
    const remaining = round2(open - accepted);
    const repl = s.outcome === 'replacement' ? Math.min(rejected, Math.max(0, remaining)) : 0;
    const wo = s.outcome === 'writeoff' ? Math.min(rejected, Math.max(0, remaining)) : 0;
    const rest = Math.max(0, round2(remaining - repl - wo));
    const hasCc = !!(l.costCenterId || l.costCenter);
    const needsPrice = l.actualUnitPrice == null;
    const issues: string[] = [];
    const tag = `Line ${l.lineNo}`;
    if (arrived > 0) {
      if (rejected < 0 || rejected > arrived) issues.push(`${tag}: rejected must be between 0 and what arrived.`);
      if (accepted > open + 1e-9) issues.push(`${tag}: ${accepted} accepted is more than the ${open} still open.`);
      if (rejected > 0) {
        if (!s.reason) issues.push(`${tag}: say why ${rejected} were rejected.`);
        if (!s.outcome) issues.push(`${tag}: choose what happens to the rejected goods.`);
        if (s.outcome === 'replacement' && !s.dueDate) issues.push(`${tag}: when is the replacement due?`);
        if (s.outcome === 'discount' && !(s.amount && s.amount > 0)) issues.push(`${tag}: enter the discount amount.`);
        if (s.outcome === 'writeoff' && !s.outcomeNote.trim()) issues.push(`${tag}: say why it is written off.`);
      }
      if (!(s.price && s.price > 0)) issues.push(`${tag}: enter the price paid.`);
      if (!hasCc && !s.ccId) issues.push(`${tag}: choose where it goes (job or store).`);
      if (rest > 0) {
        if (!s.restAction) issues.push(`${tag}: ${rest} is still open. Choose "still coming" or "close the rest".`);
        if (s.restAction === 'coming' && !s.restDate) issues.push(`${tag}: when is the rest expected?`);
        if (s.restAction === 'close' && !s.restReason.trim()) issues.push(`${tag}: say why the rest is closed.`);
      }
    }
    return { l, s, open, arrived, rejected, accepted, rest, hasCc, needsPrice, issues, value: accepted * (s.price || 0) };
  }).filter(Boolean) as any[], [openLines, st]);

  const back = daysBack(receivedOn);
  const arriving = plan.filter((p) => p.arrived > 0);
  const problems = [
    ...(!receivedOn ? ['Choose the date the goods arrived.'] : []),
    ...(back > REASON_AFTER_DAYS && !dateReason.trim() ? [`Received ${back} days ago: give a reason.`] : []),
    ...(!docRef.trim() && !proof.length ? ['Give the delivery note or invoice number, or add a photo.'] : []),
    ...(!arriving.length ? ['Enter what arrived on at least one line.'] : []),
    ...arriving.flatMap((p) => p.issues),
  ];
  const total = arriving.reduce((s, p) => s + p.value, 0);

  const save = async () => {
    if (problems.length) { message.warning(problems[0]); return; }
    setSaving(true);
    try {
      const lines: ReceiveLinePayload[] = arriving.map((p) => ({
        poLineId: p.l.id,
        arrivedQty: p.arrived,
        rejectedQty: p.rejected || undefined,
        rejectReason: p.rejected > 0 ? p.s.reason : undefined,
        actualUnitPrice: p.s.price,
        costCenterId: p.hasCc ? undefined : p.s.ccId,
        outcome: p.rejected > 0 && p.s.outcome ? {
          type: p.s.outcome,
          dueDate: p.s.outcome === 'replacement' ? isoDay(p.s.dueDate) : undefined,
          amount: p.s.outcome === 'discount' ? p.s.amount : undefined,
          note: p.s.outcomeNote.trim() || undefined,
        } : undefined,
        rest: p.rest > 0 && p.s.restAction ? {
          action: p.s.restAction,
          date: p.s.restAction === 'coming' ? isoDay(p.s.restDate) : undefined,
          reason: p.s.restAction === 'close' ? p.s.restReason.trim() : undefined,
          mrAction: p.s.mrAction,
        } : undefined,
      }));
      const res = await receiveGoods(po.poId, {
        receivedOn: isoDay(receivedOn),
        receivedOnReason: dateReason.trim() || undefined,
        deliveryNoteRef: docRef.trim() || undefined,
        proofFiles: proof,
        notes: notes.trim() || undefined,
        lines,
      });
      message.success(`${res.grn.grnId} saved`);
      res.warnings?.forEach((w) => message.warning(w, 8));
      onSaved(res.grn);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not save the receipt', 8);
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
      <div>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }} htmlFor="rw-date"><span style={{ color: '#cf1322' }}>*</span> Received on</label>
        <DatePicker id="rw-date" value={receivedOn} onChange={setReceivedOn} format="D MMM YYYY" allowClear={false} disabledDate={outsideReceiptWindow} style={{ width: '100%' }} inputReadOnly={compact} />
        {back > REASON_AFTER_DAYS && <Input style={{ marginTop: 6 }} placeholder={`Why ${back} days later?`} value={dateReason} onChange={(e) => setDateReason(e.target.value)} maxLength={300} />}
      </div>
      <div>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }} htmlFor="rw-doc"><span style={{ color: '#cf1322' }}>*</span> Delivery note or invoice</label>
        <Input id="rw-doc" placeholder="Number, e.g. DN-7781" value={docRef} onChange={(e) => setDocRef(e.target.value)} maxLength={100} />
        <div style={{ marginTop: 6 }}><ProofPicker value={proof} onChange={setProof} label="Or add a photo" /></div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>A number or a photo is enough.</div>
      </div>
      <div>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }} htmlFor="rw-notes">Notes</label>
        <Input.TextArea id="rw-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </div>
    </div>
  );

  const lineBlock = (p: any, i: number) => {
    const { l, s } = p;
    const unit = l.unit?.name || '';
    return (
      <div key={l.id} style={{ padding: 12, borderTop: i ? '1px solid #f0f0f0' : undefined, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 12, color: MUTED }}>Line {l.lineNo}{l.mrLine?.mrHeader?.mrId ? ` · ${l.mrLine.mrHeader.mrId}` : ''}</span>
            <div>{l.partNumber ? <code style={{ marginRight: 6 }}>{l.partNumber}</code> : null}{l.itemDescription}</div>
          </div>
          <span style={{ whiteSpace: 'nowrap', fontSize: 13 }}>Open <b>{p.open}</b> {unit}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: compact ? 'repeat(2, minmax(0, 1fr))' : '110px 110px 140px minmax(0, 1fr) 110px', gap: 10, alignItems: 'start' }}>
          <label style={lab}>Arrived
            <InputNumber min={0} value={s.arrived} onChange={(v) => set(l.id, { arrived: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} />
          </label>
          <label style={lab}>Rejected
            <InputNumber min={0} value={s.rejected} onChange={(v) => set(l.id, { rejected: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} />
          </label>
          <label style={lab}>Price paid, each{p.needsPrice ? ' *' : ''}
            <InputNumber min={0} precision={2} value={s.price} status={s.price ? undefined : 'error'} onChange={(v) => set(l.id, { price: v == null ? null : Number(v) })} inputMode="decimal" style={{ width: '100%' }} />
          </label>
          <div style={{ ...lab, gridColumn: compact ? '1 / -1' : undefined }}>Goes to
            {p.hasCc
              ? <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.88)' }}><code>{l.costCenter?.code}</code> {l.costCenter?.name || ''}</span>
              : <CostCenterSelect value={s.ccId} onChange={(id) => set(l.id, { ccId: id })} placeholder="Choose job or store (required)" status={s.ccId ? undefined : 'error'} />}
          </div>
          {!compact && <div style={{ textAlign: 'right', fontWeight: 600, paddingTop: 18 }}>{money(p.value)}</div>}
        </div>

        {p.rejected > 0 && (
          <div style={{ border: '1px solid #ffccc7', background: '#fff2f0', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b style={{ fontSize: 13 }}>{p.rejected} {unit} rejected</b>
            <label style={lab}>Why
              <Select value={s.reason} onChange={(v) => set(l.id, { reason: v })} options={REJECT_REASONS} placeholder="Choose a reason" style={{ maxWidth: 260 }} />
            </label>
            <div style={lab}>What happens next
              <Radio.Group value={s.outcome} onChange={(e) => set(l.id, { outcome: e.target.value })} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {OUTCOME_OPTIONS.map((o) => (
                  <Radio key={o.value} value={o.value}><span style={{ color: 'rgba(0,0,0,0.88)' }}>{o.label}</span> <span style={{ fontSize: 12, color: MUTED }}>· {o.hint}</span></Radio>
                ))}
              </Radio.Group>
            </div>
            {s.outcome === 'replacement' && (
              <label style={lab}>Replacement due
                <DatePicker value={s.dueDate} onChange={(d) => set(l.id, { dueDate: d })} format="D MMM YYYY" disabledDate={(d) => d.isBefore(receivedOn || dayjs(), 'day')} style={{ maxWidth: 220 }} inputReadOnly={compact} />
              </label>
            )}
            {s.outcome === 'discount' && (
              <label style={lab}>Discount amount (EGP)
                <InputNumber min={0} precision={2} value={s.amount} onChange={(v) => set(l.id, { amount: v == null ? null : Number(v) })} inputMode="decimal" style={{ maxWidth: 220 }} />
              </label>
            )}
            <label style={lab}>{s.outcome === 'writeoff' ? 'Why written off (goes to Ahmed)' : 'Note'}
              <Input value={s.outcomeNote} onChange={(e) => set(l.id, { outcomeNote: e.target.value })} placeholder={s.outcome === 'writeoff' ? 'Required' : 'Optional'} maxLength={300} />
            </label>
          </div>
        )}

        {p.arrived > 0 && p.rest > 0 && (
          <div style={{ border: '1px solid #ffe58f', background: '#fffbe6', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b style={{ fontSize: 13 }}>The rest: {p.rest} {unit} still open</b>
            <Radio.Group value={s.restAction} onChange={(e) => set(l.id, { restAction: e.target.value })} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Radio value="coming">Still coming</Radio>
              <Radio value="close">Close the rest <span style={{ fontSize: 12, color: MUTED }}>· asks Ahmed</span></Radio>
            </Radio.Group>
            {s.restAction === 'coming' && (
              <label style={lab}>Expected by
                <DatePicker value={s.restDate} onChange={(d) => set(l.id, { restDate: d })} format="D MMM YYYY" disabledDate={(d) => d.isBefore(receivedOn || dayjs(), 'day')} style={{ maxWidth: 220 }} inputReadOnly={compact} />
              </label>
            )}
            {s.restAction === 'close' && (
              <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'minmax(0, 1fr) 220px', gap: 8 }}>
                <Input value={s.restReason} onChange={(e) => set(l.id, { restReason: e.target.value })} placeholder="Why close the rest? (required)" maxLength={300} />
                <Select value={s.mrAction} onChange={(v) => set(l.id, { mrAction: v })} options={[{ value: 'not_needed', label: 'Requester: no longer needed' }, { value: 'reorder', label: 'Requester: order it again' }]} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {header}
      {openLines.length === 0 ? (
        <div style={{ color: MUTED }}>Nothing is open on this PO.</div>
      ) : (
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, overflow: 'hidden' }}>{plan.map(lineBlock)}</div>
      )}
      {lockedLines.length > 0 && (
        <div style={{ fontSize: 12, color: MUTED }}>
          Waiting for Ahmed's decision, so not receivable now: {lockedLines.map((l: any) => `line ${l.lineNo}`).join(', ')}.
        </div>
      )}
      <div style={{ fontSize: 12, color: MUTED }}>Set Arrived to 0 for a line that did not come.</div>
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{ fontSize: 12, color: MUTED }}>Arriving now: {arriving.length} line{arriving.length === 1 ? '' : 's'}</div>
          <div style={{ fontSize: 17, fontWeight: 600 }}>{money(total)} EGP</div>
        </div>
        {!compact && <Button onClick={onClose}>Cancel</Button>}
        <Button type="primary" loading={saving} disabled={problems.length > 0} onClick={save} style={compact ? { height: 44 } : undefined}>Save receipt</Button>
      </div>
      {problems.length > 0 && <div style={{ fontSize: 12, color: '#cf1322', textAlign: 'left' }}>{problems[0]}</div>}
    </div>
  );
  const title = `Receive goods · ${po?.poId || ''} · ${po?.vendor?.name || ''}`;

  if (compact) {
    return <Drawer open={open} onClose={onClose} placement="right" width="100%" title={title} footer={footer} destroyOnClose>{content}</Drawer>;
  }
  return <Modal open={open} onCancel={onClose} title={title} footer={footer} width={1040} destroyOnClose maskClosable={false}>{content}</Modal>;
};

export default ReceiveWindow;
